'use client';

import React, { useRef, useCallback, useState } from 'react';
import { type TopologyNode, type TopologyEdge } from './topologyData';

interface TopologyCanvasProps {
  nodes: TopologyNode[];
  edges: TopologyEdge[];
  selectedNode: TopologyNode | null;
  onSelectNode: (node: TopologyNode | null) => void;
  zoom: number;
  pan: { x: number; y: number };
  onPanChange: (pan: { x: number; y: number }) => void;
  showLabels: boolean;
  showSpeeds: boolean;
}

const nodeStatusColor: Record<string, string> = {
  online:      '#22c55e',
  offline:     '#ef4444',
  degraded:    '#f59e0b',
  maintenance: '#3b82f6',
};

const nodeVlanColor: Record<string, string> = {
  'WAN':     '#94a3b8',
  'VLAN-10': '#38bdf8',
  'VLAN-20': '#a78bfa',
  'VLAN-30': '#34d399',
};

const nodeTypeShape: Record<string, string> = {
  firewall:    '🛡',
  router:      '⬡',
  switch:      '◈',
  server:      '▣',
  workstation: '◻',
  ap:          '◎',
  unknown:     '?',
};

const edgeTypeStyle: Record<string, { stroke: string; dashArray: string }> = {
  fiber:    { stroke: '#38bdf8', dashArray: 'none' },
  copper:   { stroke: '#94a3b8', dashArray: 'none' },
  wireless: { stroke: '#34d399', dashArray: '6 4' },
  vpn:      { stroke: '#a78bfa', dashArray: '8 4' },
};

export default function TopologyCanvas({
  nodes, edges, selectedNode, onSelectNode,
  zoom, pan, onPanChange, showLabels, showSpeeds,
}: TopologyCanvasProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if ((e.target as Element).closest('.topology-node')) return;
    setIsDragging(true);
    setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
  }, [pan]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging) return;
    onPanChange({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  }, [isDragging, dragStart, onPanChange]);

  const handleMouseUp = useCallback(() => setIsDragging(false), []);

  const getEdgePath = (edge: TopologyEdge) => {
    const src = nodes.find(n => n.id === edge.source);
    const tgt = nodes.find(n => n.id === edge.target);
    if (!src || !tgt) return null;
    const mx = (src.x + tgt.x) / 2;
    const my = (src.y + tgt.y) / 2 - 20;
    return { src, tgt, mx, my };
  };

  const canvasWidth = 960;
  const canvasHeight = 720;

  return (
    <svg
      ref={svgRef}
      className={`w-full h-full bg-background ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      <defs>
        {/* Grid pattern */}
        <pattern id="topo-grid" width="40" height="40" patternUnits="userSpaceOnUse">
          <path d="M 40 0 L 0 0 0 40" fill="none" stroke="hsl(222 25% 14%)" strokeWidth="0.5" />
        </pattern>

        {/* Arrow marker */}
        <marker id="arrow-online" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
          <path d="M0,0 L0,6 L6,3 z" fill="#38bdf8" opacity="0.6" />
        </marker>
        <marker id="arrow-offline" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
          <path d="M0,0 L0,6 L6,3 z" fill="#ef4444" opacity="0.4" />
        </marker>

        {/* Glow filter */}
        <filter id="glow-green">
          <feGaussianBlur stdDeviation="3" result="coloredBlur" />
          <feMerge><feMergeNode in="coloredBlur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <filter id="glow-red">
          <feGaussianBlur stdDeviation="4" result="coloredBlur" />
          <feMerge><feMergeNode in="coloredBlur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <filter id="glow-selected">
          <feGaussianBlur stdDeviation="5" result="coloredBlur" />
          <feMerge><feMergeNode in="coloredBlur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>

      {/* Background grid */}
      <rect width="100%" height="100%" fill="url(#topo-grid)" />

      {/* Transformed group */}
      <g transform={`translate(${pan.x}, ${pan.y}) scale(${zoom})`}>

        {/* VLAN subnet grouping backgrounds */}
        {[
          { vlan: 'VLAN-10', x: 60,  y: 240, w: 420, h: 420, color: '#38bdf8' },
          { vlan: 'VLAN-20', x: 340, y: 240, w: 340, h: 200, color: '#a78bfa' },
          { vlan: 'VLAN-30', x: 560, y: 240, w: 260, h: 340, color: '#34d399' },
        ].map((zone) => (
          <g key={`zone-${zone.vlan}`}>
            <rect
              x={zone.x} y={zone.y} width={zone.w} height={zone.h}
              rx={16}
              fill={zone.color}
              fillOpacity={0.03}
              stroke={zone.color}
              strokeOpacity={0.12}
              strokeWidth={1}
              strokeDasharray="6 4"
            />
            <text x={zone.x + 12} y={zone.y + 20} fontSize={11} fill={zone.color} fillOpacity={0.5} fontFamily="IBM Plex Mono">
              {zone.vlan}
            </text>
          </g>
        ))}

        {/* Edges */}
        {edges.map((edge) => {
          const path = getEdgePath(edge);
          if (!path) return null;
          const { src, tgt } = path;
          const style = edgeTypeStyle[edge.type] || edgeTypeStyle.copper;
          const isConnectedToSelected = selectedNode && (edge.source === selectedNode.id || edge.target === selectedNode.id);
          const srcOffline = src.status === 'offline';
          const tgtOffline = tgt.status === 'offline';
          const isDown = srcOffline || tgtOffline;

          return (
            <g key={edge.id}>
              <line
                x1={src.x} y1={src.y}
                x2={tgt.x} y2={tgt.y}
                stroke={isDown ? '#ef4444' : isConnectedToSelected ? '#38bdf8' : style.stroke}
                strokeWidth={isConnectedToSelected ? 2 : 1.5}
                strokeOpacity={isDown ? 0.3 : isConnectedToSelected ? 0.9 : 0.35}
                strokeDasharray={isDown ? '4 4' : style.dashArray}
                markerEnd={isConnectedToSelected ? 'url(#arrow-online)' : undefined}
              />
              {showSpeeds && (
                <text
                  x={(src.x + tgt.x) / 2}
                  y={(src.y + tgt.y) / 2 - 6}
                  fontSize={9}
                  fill="#94a3b8"
                  textAnchor="middle"
                  fontFamily="IBM Plex Mono"
                >
                  {edge.speed}
                </text>
              )}
            </g>
          );
        })}

        {/* Nodes */}
        {nodes.map((node) => {
          const isSelected = selectedNode?.id === node.id;
          const isHovered = hoveredNode === node.id;
          const statusColor = nodeStatusColor[node.status] || '#94a3b8';
          const vlanColor = nodeVlanColor[node.vlan] || '#94a3b8';
          const nodeSize = node.type === 'firewall' || node.type === 'router' ? 28 :
                           node.type === 'switch' ? 24 :
                           node.type === 'server' ? 22 : 18;
          const isUnknown = node.type === 'unknown';

          return (
            <g
              key={node.id}
              className="topology-node"
              transform={`translate(${node.x}, ${node.y})`}
              style={{ cursor: 'pointer' }}
              onClick={(e) => { e.stopPropagation(); onSelectNode(isSelected ? null : node); }}
              onMouseEnter={() => setHoveredNode(node.id)}
              onMouseLeave={() => setHoveredNode(null)}
              filter={isSelected ? 'url(#glow-selected)' : node.status === 'offline' ? 'url(#glow-red)' : undefined}
            >
              {/* Outer ring — selection / hover */}
              {(isSelected || isHovered) && (
                <circle
                  r={nodeSize + 8}
                  fill="none"
                  stroke={isSelected ? '#38bdf8' : '#94a3b8'}
                  strokeWidth={isSelected ? 2 : 1}
                  strokeOpacity={isSelected ? 0.7 : 0.3}
                  strokeDasharray={isSelected ? 'none' : '4 4'}
                />
              )}

              {/* VLAN color ring */}
              <circle
                r={nodeSize + 4}
                fill="none"
                stroke={vlanColor}
                strokeWidth={1.5}
                strokeOpacity={0.25}
              />

              {/* Node body */}
              <circle
                r={nodeSize}
                fill={isUnknown ? 'hsl(38 92% 50% / 0.15)' : 'hsl(222 35% 12%)'}
                stroke={isUnknown ? '#f59e0b' : statusColor}
                strokeWidth={isSelected ? 2.5 : 1.5}
                strokeOpacity={node.status === 'offline' ? 0.4 : 0.8}
              />

              {/* Status dot */}
              <circle
                cx={nodeSize - 4}
                cy={-(nodeSize - 4)}
                r={5}
                fill={statusColor}
                stroke="hsl(222 47% 6%)"
                strokeWidth={1.5}
              />

              {/* Node icon text */}
              <text
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={nodeSize * 0.75}
                fill={node.status === 'offline' ? '#94a3b8' : vlanColor}
                fillOpacity={node.status === 'offline' ? 0.4 : 0.9}
                style={{ userSelect: 'none' }}
              >
                {nodeTypeShape[node.type] || '?'}
              </text>

              {/* Label */}
              {showLabels && (
                <text
                  y={nodeSize + 14}
                  textAnchor="middle"
                  fontSize={10}
                  fill={node.status === 'offline' ? '#64748b' : isSelected ? '#38bdf8' : '#94a3b8'}
                  fontFamily="IBM Plex Mono"
                  style={{ userSelect: 'none' }}
                >
                  {node.label}
                </text>
              )}

              {/* IP label on hover */}
              {isHovered && (
                <g transform={`translate(${nodeSize + 6}, -10)`}>
                  <rect x={0} y={-10} width={110} height={22} rx={4} fill="hsl(222 40% 9%)" stroke="hsl(222 25% 18%)" strokeWidth={1} />
                  <text x={6} y={4} fontSize={10} fill="#e2e8f0" fontFamily="IBM Plex Mono">
                    {node.ip}
                  </text>
                </g>
              )}
            </g>
          );
        })}
      </g>

      {/* Canvas click to deselect */}
      <rect
        width="100%" height="100%"
        fill="transparent"
        onClick={() => onSelectNode(null)}
        style={{ pointerEvents: 'none' }}
      />
    </svg>
  );
}